import { RouterState, createStore } from '@oneappexchange/lwc-wired';
import { FilterOption, FilterSection, L2FilterOption, LocationFilterOption } from 'ace/filterSection';
import { State } from 'zustand';
import { IS_DEVELOPMENT } from '~/modules/config';
import { Routes } from '~/modules/routes';
import { exploreStore, selectCurrentBucket } from '~/modules/stores/exploreStore';
import { i18nStore, selectLocationLabels, selectOafLabels, selectSearchLabels } from '~/modules/stores/i18nStore';
import { FilterChip } from '~/modules/stores/reviewStore';
import { routerStore } from '~/modules/stores/routerStore';
import { SearchParams, SearchState, searchStore, selectSearchParams } from '~/modules/stores/searchStore';
import { filterOptionsForType, isOptionChecked } from '../helpers/searchSortOptionsHelper';

export interface FiltersState extends State {
    apps: FilterSection[];
    consultants: FilterSection[];
    businessneeds: FilterSection[];
    products: FilterSection[];
    industries: FilterSection[];
    salesforcelabs: FilterSection[];
    agentforce: FilterSection[];
    impact: FilterSection[];
    filterKeyword: string | undefined;
    openFilterPanel: boolean;
}

export interface FilterLabels {
    filters: I18nMessages['search']['filters'];
    location: I18nMessages['location'];
    oaf: I18nMessages['oaf'];
}

export const isL2Option = (option: L2FilterOption): option is L2FilterOption => {
    // @ts-expect-error * Automated Type Error Fix
    return option.isL2Filter;
};

const filtersLabels = (): FilterLabels => {
    const { i18nStaticCache } = i18nStore.getState();
    return {
        filters: selectSearchLabels(i18nStaticCache).filters,
        location: selectLocationLabels(i18nStaticCache),
        oaf: selectOafLabels(i18nStaticCache),
    };
};

const initialFilterSections = (): FiltersState => {
    const { filterParams, expand } = selectSearchParams(searchStore.getState());
    return {
        apps: filterOptionsForType('apps', filterParams, filtersLabels(), expand),
        consultants: filterOptionsForType('consultants', filterParams, filtersLabels()),
        businessneeds: filterOptionsForType('businessneeds', filterParams, filtersLabels()),
        products: filterOptionsForType('products', filterParams, filtersLabels()),
        industries: filterOptionsForType('industries', filterParams, filtersLabels()),
        salesforcelabs: filterOptionsForType('labs', filterParams, filtersLabels()),
        impact: filterOptionsForType('impact', filterParams, filtersLabels()),
        agentforce: filterOptionsForType('businessneeds', filterParams, filtersLabels()),
        filterKeyword: undefined,
        openFilterPanel: false,
    };
};

const getLabelFromFilterOption = (value: string, options: L2FilterOption[] | FilterOption[]): string => {
    for (const currentOption of options) {
        if (currentOption.value === value) {
            // @ts-expect-error * Automated Type Error Fix
            return currentOption?.label;
        } else if (isL2Option(currentOption)) {
            // @ts-expect-error * Automated Type Error Fix
            const option = currentOption.options.find((currentL2Option) => {
                return currentL2Option.value === value;
            });
            if (option) {
                // @ts-expect-error * Automated Type Error Fix
                return option?.label;
            }
        }
    }
    // @ts-expect-error * Automated Type Error Fix
    return null;
};

const selectSectionsByKeyword = (keyword: string, type: string): FilterSection[] => {
    return (
        // @ts-expect-error * Automated Type Error Fix
        initialFilterSections()[type]?.reduce<FilterSection[]>((sections: FilterSection[], section: FilterSection) => {
            if (!['location', 'rating', 'experts'].includes(section.name)) {
                const filteredOptions = section.options.filter((option: FilterOption | L2FilterOption) => {
                    if (isL2Option(option)) {
                        let options = option.options;
                        if (!option.label?.toLowerCase().includes(keyword)) {
                            options = option.options?.filter(
                                (l2Option) =>
                                    l2Option.label?.toLowerCase().includes(keyword.toLowerCase()) ||
                                    isOptionChecked(section.selectedValue, l2Option),
                            );
                        }
                        if (options && options.length === 0) {
                            option.options = [];
                            return (
                                option.label?.toLowerCase().includes(keyword) ||
                                isOptionChecked(section.selectedValue, option)
                            );
                        }
                        option.options = options;
                        option.toggleDefault = true;
                        return true;
                    }

                    return (
                        option.label?.toLowerCase().includes(keyword.toLowerCase()) ||
                        isOptionChecked(section.selectedValue, option)
                    );
                });
                if (filteredOptions.length > 0) {
                    section.options = filteredOptions;
                    section.toggleDefault = true;
                    sections.push(section);
                }
            }
            return sections;
        }, []) ?? []
    );
};

const isLocationFilter = (
    options: LocationFilterOption[] | FilterOption[] | L2FilterOption[] | undefined,
): options is LocationFilterOption[] => {
    return (options && options.some((option) => 'states' in option)) || false;
};

export const selectFiltersByType = (state: FiltersState): FilterSection[] => {
    const selectedType = selectSearchParams(searchStore.getState()).type || 'apps';
    if (state.filterKeyword) {
        return selectSectionsByKeyword(state.filterKeyword, selectedType);
    }
    // @ts-expect-error * Automated Type Error Fix
    return state[selectedType];
};

export const selectFiltersByExploreType = (state: FiltersState): FilterSection[] => {
    const selectedType = selectCurrentBucket(exploreStore.getState()).replace('-', '') || 'apps';
    if (state.filterKeyword) {
        return selectSectionsByKeyword(state.filterKeyword, selectedType);
    }
    // @ts-expect-error * Automated Type Error Fix
    return state[selectedType];
};

export const selectFilterChipsByType = (): FilterChip[] => {
    const selectedType = selectSearchParams(searchStore.getState()).type || 'apps';
    // @ts-expect-error * Automated Type Error Fix
    return initialFilterSections()[selectedType].flatMap((filter: FilterSection) => {
        if (filter.selectedValue) {
            // handle location chips
            if (filter.name === 'location' && !Array.isArray(filter.selectedValue)) {
                const [country, state] = filter.selectedValue.split('-');
                const locationChips = [
                    {
                        section: filter.name,
                        value: country,
                        label: getLabelFromFilterOption(country, filter.options),
                    },
                ];
                if (isLocationFilter(filter.options) && state?.length > 1) {
                    // @ts-expect-error * Automated Type Error Fix
                    const stateOptions = filter.options.find((option) => option.value === country).states;

                    locationChips.push({
                        section: filter.name,
                        value: state,
                        label: getLabelFromFilterOption(state, stateOptions),
                    });
                }
                return locationChips;
            }
            // handle other chips
            return [filter.selectedValue].flat().map((value) => ({
                section: filter.name,
                value,
                label: getLabelFromFilterOption(value, filter.options),
            }));
        }
        return [];
    });
};

export const isFiltered = (state: FiltersState): boolean => {
    return Boolean(state.filterKeyword);
};

export const filtersStore = createStore(
    initialFilterSections(),
    (set: SetState<FiltersState>, get: GetState<FiltersState>) => ({
        setFilterStatus() {
            set(function setFilterStatus(draft) {
                return {
                    ...initialFilterSections(),
                    filterKeyword: get().filterKeyword,
                    openFilterPanel: draft.openFilterPanel,
                };
            });
        },
        searchOptions(keyword: string) {
            if (keyword) {
                set(function updateFilters(draft: FiltersState) {
                    const type = selectSearchParams(searchStore.getState()).type || 'apps';
                    // @ts-expect-error * Automated Type Error Fix
                    draft[type] = selectSectionsByKeyword(keyword, type);
                    draft.filterKeyword = keyword;
                });
            } else {
                filtersStore.reset();
            }
        },
        setOpenFilterPanel(newState: boolean) {
            set(function updateOpenFilterPanel(draft) {
                draft.openFilterPanel = newState;
            });
        },
        reset() {
            set(function resetStore(draft) {
                return {
                    ...initialFilterSections(),
                    openFilterPanel: draft.openFilterPanel,
                };
            });
        },
    }),
    'filtersStore',
    IS_DEVELOPMENT,
);

searchStore.subscribe(
    (params: SearchParams) => {
        const filterChanges = params.filterParams
            ? Object.values(params.filterParams).some((filter) => filter)
            : undefined;
        if (filterChanges) {
            filtersStore.setFilterStatus();
            return;
        }
        filtersStore.reset();
    },
    (state: SearchState) => state.searchParams,
);

routerStore.subscribe(
    (url) => {
        if (!url.includes(Routes.SEARCH) && filtersStore.getState().openFilterPanel) {
            filtersStore.setOpenFilterPanel(false);
        }
    },
    (state: RouterState) => state.url,
);
